/**
 * OASIS applicant document upload policy — which file formats each document
 * category accepts, and the per-file size cap.
 *
 * SINGLE SOURCE OF TRUTH. Copied verbatim between the two repositories:
 *   API: doj_asb-cap_api/src/_shared/oasis/upload-policy.ts
 *   UI:  doj_asb-cap_ui/projects/shared-resources/src/lib/oasis/oasis-upload-policy.ts
 *
 * Keep the two copies byte-identical. This file is intentionally
 * dependency-free (no imports) so that is possible — the same convention the
 * shared file-type catalog uses. Each repo has a test asserting the policy
 * covers exactly its own `OasisDocType` union, so adding a document category
 * without giving it a policy fails the build on both sides.
 *
 * Everything downstream derives from this map and nothing restates it: the
 * server's per-docType content-type and extension agreement maps, its flat MIME
 * allow list, and the applicant uploader's accept attribute, per-category
 * option gating, and error copy.
 *
 * Derived from the OARM document list: law transcripts PDF only; fellowship /
 * Non-DOJ Honors offer letters PDF or Word; DD-214, disability preference, and
 * derivative preference PDF only.
 *
 * Extensions — not MIME types — are the authoritative gate. Browsers report
 * `application/octet-stream` when they cannot type a file, and that value is
 * accepted for every extension, so narrowing a category by content type alone
 * would let an untyped `.docx` into a PDF-only category.
 */

export interface OasisUploadPolicy {
	/** Per-file size cap in bytes. */
	readonly maxUploadBytes: number;
	/**
	 * docType → accepted extensions, lowercased and undotted (e.g. `['pdf']`),
	 * matching the shared file-type catalog's key format.
	 */
	readonly docTypeExtensions: Readonly<Record<string, readonly string[]>>;
}

export const OASIS_UPLOAD_POLICY: OasisUploadPolicy = {
	// 25 MB covers transcripts and offer letters without inviting abuse.
	maxUploadBytes: 25 * 1024 * 1024,
	docTypeExtensions: {
		law_transcript: ['pdf'],
		fellowship_offer_letter: ['pdf', 'doc', 'docx'],
		dd214: ['pdf'],
		disability_preference: ['pdf'],
		derivative_preference: ['pdf'],
	},
};

/** The document categories the policy covers. */
export const OASIS_POLICY_DOC_TYPES: readonly string[] = Object.keys(OASIS_UPLOAD_POLICY.docTypeExtensions);

/** Every extension accepted by at least one category, undotted. */
export const OASIS_ACCEPTED_EXTENSIONS: readonly string[] = [...new Set(Object.values(OASIS_UPLOAD_POLICY.docTypeExtensions).flat())];

/** Dotted form of {@link OASIS_ACCEPTED_EXTENSIONS} (e.g. `['.pdf']`), for file-picker `accept`. */
export const OASIS_ACCEPTED_EXTENSIONS_DOTTED: readonly string[] = OASIS_ACCEPTED_EXTENSIONS.map((ext) => `.${ext}`);

/** Dotted form of the per-docType map, for clients that work in dotted extensions. */
export const OASIS_DOC_TYPE_EXTENSIONS_DOTTED: Readonly<Record<string, readonly string[]>> = Object.fromEntries(
	Object.entries(OASIS_UPLOAD_POLICY.docTypeExtensions).map(([docType, exts]) => [docType, exts.map((ext) => `.${ext}`)]),
);
